/**
 * 顶栏组件
 * 显示连接状态、串口选择、波特率选择、连接按钮等快速操作
 */
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Cable, Menu, PanelLeftClose, PanelLeftOpen, X, Zap, ZapOff } from 'lucide-react';
import { useConnectionStore } from '@/stores/connection';
import { useTelemetryStore } from '@/stores/telemetry';
import { useWebSocket } from '@/hooks/use-websocket';
import { PortSelector } from '@/components/connection/port-selector';
import { BaudSelector } from '@/components/connection/baud-selector';
import { ConnectButton } from '@/components/connection/connect-button';
import { appRoutes } from './routes';

interface HeaderProps {
  sidebarCollapsed: boolean;
  onToggleSidebar: () => void;
  onOpenMobileNav: () => void;
}

const stateColors: Record<string, string> = {
  connected: 'text-motor-green',
  connecting: 'text-motor-yellow',
  reconnecting: 'text-motor-yellow',
  disconnected: 'text-surface-500',
};

const stateLabels: Record<string, string> = {
  connected: '已连接',
  connecting: '连接中…',
  reconnecting: '重连中…',
  disconnected: '未连接',
};

/** 顶栏组件：显示连接状态指示、串口/波特率选择与连接按钮等快速操作 */
export function Header({ sidebarCollapsed, onToggleSidebar, onOpenMobileNav }: HeaderProps) {
  const [devicePanelOpen, setDevicePanelOpen] = useState(false);
  const location = useLocation();
  const connectionState = useConnectionStore((s) => s.state);
  const status = useTelemetryStore((s) => s.status);
  const { isConnected: gatewayConnected } = useWebSocket();
  const deviceConnected = connectionState === 'connected';
  const currentRoute = appRoutes.find((route) => route.path === location.pathname) || appRoutes[0];

  const connectionIndicator = (
    <div className="flex h-9 items-center gap-2 rounded border border-surface-700 bg-surface-800/70 px-3">
      {gatewayConnected && deviceConnected ? (
        <Zap className="h-3.5 w-3.5 text-accent-400" />
      ) : (
        <ZapOff className="h-3.5 w-3.5 text-surface-500" />
      )}
      <span className={`text-xs font-semibold ${!gatewayConnected ? 'text-red-400' : stateColors[connectionState] || 'text-surface-500'}`}>
        {!gatewayConnected ? '网关离线' : stateLabels[connectionState] || connectionState}
      </span>
    </div>
  );

  return (
    <header className="relative z-30 flex h-[72px] shrink-0 items-center gap-3 border-b border-surface-700/80 bg-surface-900 px-4 sm:px-5 lg:px-6">
      <button
        aria-label="打开导航"
        onClick={onOpenMobileNav}
        className="rounded-md p-2 text-surface-400 transition-colors hover:bg-surface-800 hover:text-surface-100 lg:hidden"
      >
        <Menu className="h-5 w-5" />
      </button>

      <button
        aria-label={sidebarCollapsed ? '展开侧边栏' : '收起侧边栏'}
        title={sidebarCollapsed ? '展开侧边栏' : '收起侧边栏'}
        onClick={onToggleSidebar}
        className="hidden rounded-md p-2 text-surface-500 transition-colors hover:bg-surface-800 hover:text-surface-100 lg:inline-flex"
      >
        {sidebarCollapsed ? <PanelLeftOpen className="h-5 w-5" /> : <PanelLeftClose className="h-5 w-5" />}
      </button>

      <div className="min-w-0">
        <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-accent-400">Control workspace</div>
        <h1 className="truncate text-base font-semibold text-surface-100 sm:text-lg">{currentRoute.label}</h1>
      </div>

      <div className="flex-1" />

      <div className="hidden items-center gap-2 lg:flex">
        {status && (
          <div className="mr-1 flex h-9 items-center gap-2 rounded border border-surface-700 bg-surface-800/70 px-3">
            <div
              className={`h-2 w-2 rounded-full ${
                status.state === 'run'
                  ? 'bg-accent-400 animate-pulse'
                  : status.state === 'fault'
                  ? 'bg-motor-red animate-pulse'
                  : status.state === 'calibration'
                  ? 'bg-motor-yellow'
                  : 'bg-surface-500'
              }`}
            />
            <span className="text-xs font-medium text-surface-300">
              {status.state === 'run' ? '运行中' : status.state === 'fault' ? '故障' : status.state === 'calibration' ? '校准中' : '待机'}
            </span>
          </div>
        )}

        <PortSelector />
        <BaudSelector />
        <ConnectButton />
        {connectionIndicator}
      </div>

      <button
        aria-expanded={devicePanelOpen}
        aria-label="设备连接"
        onClick={() => setDevicePanelOpen(!devicePanelOpen)}
        className="inline-flex h-9 items-center gap-2 rounded border border-surface-700 bg-surface-800 px-3 text-xs font-semibold text-surface-300 hover:border-surface-600 hover:text-surface-100 lg:hidden"
      >
        <Cable className={`h-4 w-4 ${deviceConnected ? 'text-accent-400' : 'text-surface-500'}`} />
        <span className="hidden sm:inline">设备</span>
      </button>

      {devicePanelOpen && (
        <div className="absolute right-3 top-[64px] z-50 w-[min(340px,calc(100vw-24px))] rounded-lg border border-surface-700 bg-surface-900 p-4 shadow-2xl lg:hidden">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-surface-100">设备连接</div>
              <div className="mt-0.5 text-xs text-surface-500">STM32 串口网关</div>
            </div>
            <button
              aria-label="关闭设备面板"
              onClick={() => setDevicePanelOpen(false)}
              className="rounded p-1.5 text-surface-500 hover:bg-surface-800 hover:text-surface-200"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="grid gap-3 [&>button]:w-full">
            <PortSelector />
            <BaudSelector />
            <div className="flex items-center justify-between gap-3 border-t border-surface-700 pt-3">
              {connectionIndicator}
              <ConnectButton />
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
